import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { techAPI } from '../../lib/api';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  ChevronLeft, ChevronRight, MapPin, Phone, Navigation,
  Loader2, Calendar as CalendarIcon, Truck, CheckCircle2,
  AlertTriangle,
} from 'lucide-react';

const STATUS_COLORS = {
  scheduled: 'border-l-slate-400',
  en_route:  'border-l-blue-500',
  on_site:   'border-l-amber-500',
  done:      'border-l-emerald-500',
};
const STATUS_BG = {
  scheduled: 'bg-white',
  en_route:  'bg-blue-50',
  on_site:   'bg-amber-50',
  done:      'bg-emerald-50/50',
};
const STATUS_LABELS = {
  scheduled: 'Scheduled',
  en_route:  'En Route',
  on_site:   'On Site',
  done:      'Complete',
};
const STATUS_BADGE = {
  scheduled: 'bg-slate-100 text-slate-600',
  en_route:  'bg-blue-100 text-blue-700',
  on_site:   'bg-amber-100 text-amber-800',
  done:      'bg-emerald-100 text-emerald-700',
};
const WORKFLOW_ACTIONS = {
  scheduled: { label: 'On My Way',  icon: Truck,       next: 'en_route', color: 'bg-blue-600 text-white active:bg-blue-700' },
  en_route:  { label: 'Arrived',    icon: MapPin,       next: 'on_site',  color: 'bg-amber-500 text-slate-950 active:bg-amber-600' },
  on_site:   { label: 'Finish',     icon: CheckCircle2, next: 'done',     color: 'bg-emerald-600 text-white active:bg-emerald-700' },
};

function formatDate(d) {
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}
function toYMD(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function isToday(d) {
  const now = new Date();
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
}

export default function TechSchedule() {
  const navigate = useNavigate();
  const [date, setDate] = useState(new Date());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (d) => {
    setLoading(true);
    try {
      const res = await techAPI.myDay(toYMD(d));
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(date); }, [date, load]);

  const prev = () => setDate(d => { const n = new Date(d); n.setDate(n.getDate() - 1); return n; });
  const next = () => setDate(d => { const n = new Date(d); n.setDate(n.getDate() + 1); return n; });
  const today = () => setDate(new Date());

  const handleInlineStatus = async (e, eventId, newStatus) => {
    e.stopPropagation();
    try {
      await techAPI.updateStatus(eventId, newStatus);
      setData(prev => {
        if (!prev) return prev;
        const events = prev.events.map(ev =>
          ev.event_id === eventId ? { ...ev, status: newStatus } : ev
        );
        return { ...prev, events, completed_events: events.filter(ev => ev.status === 'done').length };
      });
      toast.success(STATUS_LABELS[newStatus]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  return (
    <div className="max-w-lg mx-auto" data-testid="tech-schedule">
      {/* ── Date header ── */}
      <div className="sticky top-0 z-10 bg-slate-50 border-b-2 border-slate-900 px-4 py-3">
        <div className="flex items-center justify-between">
          <button onClick={prev} className="p-2.5 -ml-2 rounded-lg active:bg-slate-200" data-testid="schedule-prev">
            <ChevronLeft className="w-5 h-5 text-slate-600" strokeWidth={2.5} />
          </button>

          <div className="text-center">
            <h2 className="text-base font-bold text-slate-950" data-testid="schedule-date">
              {isToday(date) ? 'Today' : formatDate(date)}
            </h2>
            {!isToday(date) && (
              <button onClick={today} className="text-[10px] font-bold text-blue-600 uppercase tracking-wide mt-0.5">
                Jump to Today
              </button>
            )}
            {isToday(date) && (
              <p className="text-[10px] text-slate-400 font-medium">{formatDate(date)}</p>
            )}
          </div>

          <button onClick={next} className="p-2.5 -mr-2 rounded-lg active:bg-slate-200" data-testid="schedule-next">
            <ChevronRight className="w-5 h-5 text-slate-600" strokeWidth={2.5} />
          </button>
        </div>

        {data && data.total_events > 0 && (
          <div className="flex justify-center gap-4 mt-2 text-[10px] uppercase tracking-wide font-bold">
            <span className="text-slate-500">{data.total_events} jobs</span>
            <span className="text-emerald-600">{data.completed_events} done</span>
            <span className="text-blue-600">{data.total_events - data.completed_events} remaining</span>
          </div>
        )}
      </div>

      {/* ── Event list ── */}
      <div className="px-4 py-4">
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : !data || data.total_events === 0 ? (
          <div className="text-center py-16">
            <CalendarIcon className="w-10 h-10 text-slate-200 mx-auto mb-3" />
            <p className="text-base font-bold text-slate-400">No jobs scheduled</p>
            <p className="text-sm text-slate-300 mt-1">{formatDate(date)}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {data.events.map(evt => {
              const cust = evt.customer;
              const time = evt.start_at ? new Date(evt.start_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '';
              const endTime = evt.end_at ? new Date(evt.end_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '';
              const action = WORKFLOW_ACTIONS[evt.status];

              return (
                <div key={evt.event_id}
                  className={`rounded-lg border-l-4 border border-slate-200 overflow-hidden ${STATUS_COLORS[evt.status]} ${STATUS_BG[evt.status]}`}
                  data-testid={`schedule-event-${evt.event_id}`}
                >
                  {/* Tappable main area */}
                  <button
                    onClick={() => navigate(`/tech/jobs/${evt.event_id}`)}
                    className="w-full text-left p-4 active:bg-black/5"
                  >
                    {/* Time + Status row */}
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm font-bold font-mono text-slate-950">
                        {time}{endTime ? ` — ${endTime}` : ''}
                      </span>
                      <div className="flex items-center gap-1.5">
                        {evt.priority === 'high' && <AlertTriangle className="w-3 h-3 text-red-500" />}
                        <Badge className={`text-[10px] font-bold uppercase tracking-wide ${STATUS_BADGE[evt.status]}`}>
                          {STATUS_LABELS[evt.status]}
                        </Badge>
                      </div>
                    </div>

                    {/* Job name */}
                    <p className="text-base font-bold text-slate-950 leading-snug">
                      {evt.job_name || evt.description || `Job #${evt.job_number || ''}`}
                    </p>

                    {/* Customer + address */}
                    {cust && (
                      <div className="mt-1.5">
                        <p className="text-sm text-slate-600">{cust.name}</p>
                        {cust.address && (
                          <p className="text-xs text-slate-400 flex items-start gap-1 mt-0.5">
                            <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
                            <span className="truncate">{cust.address}</span>
                          </p>
                        )}
                      </div>
                    )}
                  </button>

                  {/* Quick action bar (only for non-done events) */}
                  {evt.status !== 'done' && (
                    <div className="flex items-stretch border-t border-slate-200">
                      {cust?.address && (
                        <a href={`https://maps.google.com/?q=${encodeURIComponent(cust.address)}`}
                           target="_blank" rel="noreferrer"
                           className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-bold text-blue-700 active:bg-blue-50 border-r border-slate-200"
                           onClick={e => e.stopPropagation()}>
                          <Navigation className="w-3.5 h-3.5" strokeWidth={2.5} /> Navigate
                        </a>
                      )}
                      {cust?.phone && (
                        <a href={`tel:${cust.phone}`}
                           className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-bold text-slate-600 active:bg-slate-50 border-r border-slate-200"
                           onClick={e => e.stopPropagation()}>
                          <Phone className="w-3.5 h-3.5" strokeWidth={2.5} /> Call
                        </a>
                      )}
                      {action && (
                        <button
                          onClick={(e) => handleInlineStatus(e, evt.event_id, action.next)}
                          className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-bold ${action.color}`}>
                          <action.icon className="w-3.5 h-3.5" strokeWidth={2.5} /> {action.label}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
